/**
 * Standard Card Component
 *
 * Displays an official standard with status badge and contextual actions.
 * Actions are contextual based on the standard's current status.
 */

// TODO: Add proper admin role check before allowing mutations

import { useNavigate, useParams } from "@tanstack/react-router";
import {
  Eye,
  CheckCircle,
  XCircle,
  Trash2,
  ArrowDownCircle,
  FileText,
} from "lucide-react";
import { api } from "convex/_generated/api";
import { Button } from "@/components/ui/primitives";
import { useApiMutation } from "@/packages/features-shared";
import type { FunctionReturnType } from "convex/server";

type OfficialStandard = FunctionReturnType<
  typeof api.admin_standards_queries.list
>[number];

type StandardStatus = OfficialStandard["status"];

/* ------------------------------------------------------------------ */
/* Status badge configuration                                          */
/* ------------------------------------------------------------------ */

const STATUS_CONFIG: Record<
  StandardStatus,
  { label: string; bgColor: string; textColor: string; pulse?: boolean }
> = {
  uploading: {
    label: "Uploading",
    bgColor: "rgba(161,161,166,0.15)",
    textColor: "#a1a1a6",
  },
  extracting: {
    label: "Extracting",
    bgColor: "rgba(234,179,8,0.15)",
    textColor: "#ca8a04",
    pulse: true,
  },
  extracted: {
    label: "Extracted",
    bgColor: "rgba(59,130,246,0.15)",
    textColor: "#3b82f6",
  },
  extraction_failed: {
    label: "Extraction Failed",
    bgColor: "rgba(239,68,68,0.15)",
    textColor: "#ef4444",
  },
  approved: {
    label: "Approved",
    bgColor: "rgba(99,102,241,0.15)",
    textColor: "#6366f1",
  },
  generating_checklist: {
    label: "Generating Checklist",
    bgColor: "rgba(234,179,8,0.15)",
    textColor: "#ca8a04",
    pulse: true,
  },
  checklist_generation_failed: {
    label: "Checklist Generation Failed",
    bgColor: "rgba(239,68,68,0.15)",
    textColor: "#ef4444",
  },
  published: {
    label: "Published",
    bgColor: "rgba(34,197,94,0.15)",
    textColor: "#22c55e",
  },
  rejected: {
    label: "Rejected",
    bgColor: "rgba(239,68,68,0.15)",
    textColor: "#ef4444",
  },
};

function StatusBadge({ status }: { status: StandardStatus }) {
  const config = STATUS_CONFIG[status];
  return (
    <span
      className={`inline-block rounded-full px-2.5 py-0.5 text-[11px] font-medium ${config.pulse ? "animate-pulse" : ""}`}
      style={{ backgroundColor: config.bgColor, color: config.textColor }}
    >
      {config.label}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Date formatter                                                      */
/* ------------------------------------------------------------------ */

function formatDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/* ------------------------------------------------------------------ */
/* Standard Card                                                       */
/* ------------------------------------------------------------------ */

interface StandardCardProps {
  standard: OfficialStandard;
}

export function StandardCard({ standard }: StandardCardProps) {
  const navigate = useNavigate();
  const { workspaceSlug } = useParams({ strict: false });
  const approveMutation = useApiMutation(
    api.admin_standards_mutations.approveExtraction,
    {
      name: "approve-extraction",
      successMessage: "Extraction approved. Generating checklist...",
    },
  );
  const rejectMutation = useApiMutation(
    api.admin_standards_mutations.rejectExtraction,
    { name: "reject-extraction", successMessage: "Extraction rejected" },
  );
  const unpublishMutation = useApiMutation(
    api.admin_standards_mutations.unpublish,
    { name: "unpublish-standard", successMessage: "Standard unpublished" },
  );
  const deleteMutation = useApiMutation(
    api.admin_standards_mutations.deleteStandard,
    { name: "delete-standard", successMessage: "Standard deleted" },
  );

  const handleView = () => {
    navigate({
      to: "/$workspaceSlug/admin/standards/$standardId",
      params: { workspaceSlug: workspaceSlug ?? "", standardId: standard._id },
    });
  };

  const handleApprove = async () => {
    await approveMutation.mutateAsync({ standardId: standard._id });
  };

  const handleReject = async () => {
    await rejectMutation.mutateAsync({ standardId: standard._id });
  };

  const handleUnpublish = async () => {
    await unpublishMutation.mutateAsync({ standardId: standard._id });
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this standard?"))
      return;
    await deleteMutation.mutateAsync({ standardId: standard._id });
  };

  /* ---- Contextual action buttons ---- */
  const actions = getContextualActions(standard.status, {
    onView: handleView,
    onApprove: handleApprove,
    onReject: handleReject,
    onUnpublish: handleUnpublish,
    onDelete: handleDelete,
  });

  /* ---- Counts display ---- */
  const hasCounts =
    standard.extractedClausesCount > 0 || standard.checklistItemsCount > 0;

  return (
    <div className="rounded-xl border border-border bg-card p-4 transition-colors hover:bg-card-hover/30">
      {/* Row 1: Template code + Status badge */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <FileText size={16} className="text-accent/70 shrink-0" />
          <span className="text-[14px] font-semibold text-primary">
            {standard.templateCode}
          </span>
          {standard.templateDisplayName && (
            <span className="text-[12px] text-muted">
              {standard.templateDisplayName}
            </span>
          )}
        </div>
        <StatusBadge status={standard.status} />
      </div>

      {/* Row 2: File name */}
      <p className="text-[13px] text-secondary truncate mb-2">
        {standard.fileName}
      </p>

      {/* Row 3: Counts + Date + Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {hasCounts && (
            <span className="text-[12px] text-muted">
              {standard.extractedClausesCount > 0 &&
                `${standard.extractedClausesCount} clauses`}
              {standard.extractedClausesCount > 0 &&
                standard.checklistItemsCount > 0 &&
                " / "}
              {standard.checklistItemsCount > 0 &&
                `${standard.checklistItemsCount} items`}
            </span>
          )}
          <span className="text-[12px] text-muted">
            {formatDate(standard._creationTime)}
          </span>
        </div>

        <div className="flex items-center gap-1">
          {actions.map((action) => (
            <Button
              key={action.label}
              variant={action.variant}
              size="sm"
              icon={action.icon}
              onClick={action.onClick}
            >
              {action.label}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Contextual action helpers                                           */
/* ------------------------------------------------------------------ */

interface ActionHandlers {
  onView: () => void;
  onApprove: () => void;
  onReject: () => void;
  onUnpublish: () => void;
  onDelete: () => void;
}

interface ActionConfig {
  label: string;
  icon: typeof Eye;
  variant: "secondary" | "primary" | "ghost" | "danger";
  onClick: () => void;
}

function getContextualActions(
  status: StandardStatus,
  handlers: ActionHandlers,
): ActionConfig[] {
  const viewAction: ActionConfig = {
    label: "View",
    icon: Eye,
    variant: "ghost",
    onClick: handlers.onView,
  };

  switch (status) {
    case "extracted":
      return [
        viewAction,
        {
          label: "Approve",
          icon: CheckCircle,
          variant: "primary",
          onClick: handlers.onApprove,
        },
        {
          label: "Reject",
          icon: XCircle,
          variant: "danger",
          onClick: handlers.onReject,
        },
      ];
    case "approved":
    case "generating_checklist":
      return [viewAction];
    case "published":
      return [
        viewAction,
        {
          label: "Unpublish",
          icon: ArrowDownCircle,
          variant: "secondary",
          onClick: handlers.onUnpublish,
        },
      ];
    case "rejected":
    case "extraction_failed":
    case "checklist_generation_failed":
      return [
        viewAction,
        {
          label: "Delete",
          icon: Trash2,
          variant: "danger",
          onClick: handlers.onDelete,
        },
      ];
    default:
      return [viewAction];
  }
}
